/*
 * Model for Action API Endpoints
 */
define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel) {
  var ApiConfigModel = {};

  ApiConfigModel.apiConfig = Backbone.Model.extend({
    idAttribute: "apiId",
    defaults: {
      name: "",
      endpointUrl: "",
      contentType: "JSON",
      authenticationType: 0,
      rateLimit: 1,
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        //API name must be less than 100 chars
        if (value.length === 0) return $.t("commonMessages.emptyMessage");
        if (value.length > 100)
          return $.t("commonMessages.tooLong", { number: 100, current: value.length });
      }
      if (attribute === "endpointUrl") {
        //URI Endpoint must be less than 500 chars
        if (value.length === 0) return $.t("commonMessages.emptyMessage");
        if (value.length > 500)
          return $.t("commonMessages.tooLong", { number: 500, current: value.length });
      }
      if (attribute === "rateLimit") {
        if (value.length === 0) return $.t("commonMessages.emptyMessage");
        if (isNaN(value) || !isFinite(value)) return $.t("commonMessages.notNumeric");
        if (parseInt(value) != value) return $.t("commonMessages.notInteger");
        if (value <= 0) return $.t("commonMessages.notPositive");
        //limit the rate lmit to 60 rpm for non global admins
        var isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
        if (!isGlobalAdmin && value > 60) return $.t("commonMessages.tooBig", { number: 60 });
      }
    },
    //Used to switch the authentication model if the auth type has changed
    setAuthentication: function (authType) {
      if (this.get("authenticationType") !== authType) {
        this.set("authenticationType", authType);
        delete this.authentication;
        this.subModels = [];
        if (authType === 1) {
          this.authentication = new ApiConfigModel.basicAuth();
          this.subModels.push(this.authentication);
        } else if (authType == 2) {
          this.authentication = new ApiConfigModel.keyAuth();
          this.subModels.push(this.authentication);
        }
        if (this.authentication) {
          //any invalid events on the child object should propagate to the parent
          this.authentication.on(
            "invalid",
            function (model, error) {
              this.trigger("invalid", this, error);
            },
            this,
          );
        }
      }
    },
    getAuthentication: function () {
      if (this.get("authenticationType") === 1) {
        //basic auth
        this.authentication = this.getSubModel(
          this.authentication,
          ApiConfigModel.basicAuth,
          "/basicAuth",
        );
      } else if (this.get("authenticationType") === 2) {
        //key auth
        this.authentication = this.getSubModel(
          this.authentication,
          ApiConfigModel.keyAuth,
          "/keyAuth",
        );
      } else {
        //no auth
        this.authentication = new ApiConfigModel.noAuth();
      }
      //any invalid events on the child object should propagate to the parent
      this.authentication.on(
        "invalid",
        function (model, error) {
          this.trigger("invalid", this, error);
        },
        this,
      );
      return this.authentication;
    },
    saveChildren: function (options) {
      //pass all the data needed to the child models for saving
      if (this.authentication) {
        this.authentication.set("apiId", this.id);
      }
      this.saveSubModels(options);
    },
    validate: function (attributes) {
      if (!attributes || !attributes.length) {
        attributes = ["name", "endpointUrl", "rateLimit"];
      }
      const errors = {};
      let isValid = true;
      _.each(attributes, (attribute) => {
        const error = this.validateAttribute(attribute, this.get(attribute));
        if (error) {
          errors[attribute] = error;
          isValid = false;
        }
      });
      //also validate the authentication
      if (this.authentication) {
        const authValidation = this.authentication.validate();
        if (authValidation) {
          isValid = false;
          _.extend(errors, authValidation);
        }
      }
      if (!isValid) {
        return errors;
      }
    },
  });

  ApiConfigModel.apiConfigs = Backbone.Collection.extend({
    model: ApiConfigModel.apiConfig,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
    addItem: function () {
      var newItem = new ApiConfigModel.apiConfig();
      this.add(newItem);
      return newItem;
    },
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/api";
    },
  });

  ApiConfigModel.basicAuth = Backbone.Model.extend({
    idAttribute: "apiId",
    defaults: {
      user: "",
      password: "",
    },
    defaultValidateAttributes: ["user", "password"],
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/api/" + this.id + "/basicAuth";
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "user") {
        return (
          this.validateRequiredValue(value, { maxLength: 100 }) ||
          this.validateSafeCharacters(value)
        );
      }
      if (attribute === "password") {
        return this.validateRequiredValue(value, { maxLength: 100 });
      }
    },
  });

  ApiConfigModel.keyAuth = Backbone.Model.extend({
    idAttribute: "apiId",
    defaults: {
      keyLocation: 0,
      keyName: "",
      key: "",
    },
    defaultValidateAttributes: ["keyName", "key"],
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/api/" + this.id + "/keyAuth";
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "keyName") {
        return (
          this.validateRequiredValue(value, { maxLength: 500 }) ||
          this.validateSafeCharacters(value)
        );
      }
      if (attribute === "key") {
        return this.validateRequiredValue(value, { maxLength: 500 });
      }
    },
  });

  //A plain model for representing no authentication
  ApiConfigModel.noAuth = Backbone.Model.extend({});

  Messenger.reqres.setHandler(
    "apis",
    function () {
      var apis = new ApiConfigModel.apiConfigs();
      apis.fetch();
      return apis;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "api:config:headers",
    function () {
      var Header = SortableListModel.headerModel;
      return new Backbone.Collection([
        new Header({
          headerName: $.t("admin.apiAdmin.apiName"),
          modelAttribute: "name",
          selectable: true,
          sortable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.apiAdmin.uriEndpoint"),
          modelAttribute: "endpointUrl",
          longTextField: true,
          sortable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.apiAdmin.rateLimit"),
          modelAttribute: "rateLimit",
          sortable: true,
          hidden: true,
          hideable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.apiAdmin.authentication"),
          modelAttribute: "authenticationType",
          sortable: false,
          filterable: false,
          customHtml:
            "<% if (authenticationType === 1) { %> \n\
            <span data-i18n='admin.apiAdmin.authType.basicAuth'></span> \n\
          <% } else if (authenticationType === 2) { %> \n\
            <span data-i18n='admin.apiAdmin.authType.apiKey'></span> \n\
          <% } else { %> \n\
          <span data-i18n='admin.apiAdmin.authType.none'></span> \n\
          <% } %>",
        }),
        new Header({
          headerName: "",
          modelAttribute: "name",
          width: "15px",
          customHtml:
            '<button id="deleteApi" type="button" class="btn btn-danger btn-mini" data-toggle="tooltip" data-i18n="[title]admin.apiAdmin.deleteApi"> \n\
            <i class="icon-remove"></i> \n\
          </button> ',
        }),
      ]);
    },
    this,
  );

  return ApiConfigModel;
});
